import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, Eye, EyeOff, FileSpreadsheet, Loader2, RotateCw } from 'lucide-react';
import { Button } from '../ui/button';
import { klarysUrl } from './klarysApi';
export const STATUT_CAMPAGNE = {
  executee: ['Terminée', 'bg-emerald-100 text-emerald-800'],
  partiel: ['Incomplète', 'bg-amber-100 text-amber-800'],
  echec: ['Échec', 'bg-rose-100 text-rose-800'],
  programmee: ['Programmée', 'bg-violet-100 text-violet-800'],
  en_cours: ['En cours', 'bg-sky-100 text-sky-800'],
};
export const paris = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'short', timeStyle: 'short' }) : '—');

const telecharger = async (url, nom, headers) => {
  try {
    const r = await axios.get(url, { headers, responseType: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(r.data);
    a.download = nom;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch { toast.error('Téléchargement impossible'); }
};

const Ligne = ({ l, i, headers, onMaj }) => {
  const [busy, setBusy] = useState(false);
  const reessayer = async () => {
    setBusy(true);
    try { await axios.post(klarysUrl(`/membres/${l.membre_id}/envois/${l.ged_id}/reessayer`), {}, { headers }); toast.success(`Renvoyé à ${l.email}`); } catch (e) { toast.error(e.response?.data?.detail || 'Nouvel échec'); }
    setBusy(false);
    onMaj();
  };
  const ok = l.statut === 'envoye';
  return (
    <tr className="border-t border-slate-100" data-testid={`klarys-campagne-ligne-${i}`}>
      <td className="py-2 pr-2"><span className="font-medium text-slate-800">{l.membre || '—'}</span><span className="block text-[10px] text-slate-400">{l.email}</span></td>
      <td className="py-2 pr-2 text-slate-600"><span className="block max-w-[180px] truncate" title={l.document}>{l.document}</span><span className="text-[10px] text-slate-400">{paris(l.envoye_le)}{l.tentatives > 1 ? ` · ${l.tentatives} tentatives` : ''}</span></td>
      <td className="py-2 pr-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`} data-testid={`klarys-campagne-ligne-statut-${i}`}>{ok ? 'Envoyé' : 'Échec'}</span></td>
      <td className="py-2 pr-2 text-[11px]" data-testid={`klarys-campagne-ligne-lecture-${i}`}>
        {!ok ? '—' : l.ouvert ? <span className="flex items-center gap-1 text-emerald-700"><Eye className="h-3.5 w-3.5" /> {paris(l.premiere_ouverture)} ({l.ouvertures}×)</span>
          : <span className="flex items-center gap-1 text-slate-400"><EyeOff className="h-3.5 w-3.5" /> Non ouvert</span>}
      </td>
      <td className="py-2 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" className="h-7 px-2" data-testid={`klarys-campagne-ligne-telecharger-${i}`}
          onClick={() => telecharger(klarysUrl(`/membres/${l.membre_id}/envois/${l.ged_id}/fichier`), l.document || 'document', headers)}><Download className="h-3.5 w-3.5" /></Button>
        {!ok && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy} onClick={reessayer} data-testid={`klarys-campagne-ligne-reessayer-${i}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="mr-1 h-3.5 w-3.5" />} Réessayer
          </Button>
        )}
      </td>
    </tr>
  );
};

export const KlarysCampagne = ({ cid, periode, headers, onRetour }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const charger = useCallback(() => {
    axios.get(klarysUrl(`/envois/campagnes/${cid}`), { headers, params: { periode } })
      .then((r) => setC(r.data)).catch((e) => { toast.error(e.response?.data?.detail || 'Campagne introuvable'); onRetour(); });
  }, [cid, periode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [charger]);
  const toutReessayer = async () => {
    if (!window.confirm(`Renvoyer l'e-mail aux ${c.echecs} membre(s) en échec ?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(klarysUrl(`/envois/campagnes/${cid}/reessayer`), {}, { headers, params: { periode } });
      if (r.data.en_arriere_plan) {
        toast.info(`Relance de ${r.data.reessayes} envoi(s) lancée en arrière-plan : actualisation dans quelques secondes`);
        setTimeout(charger, Math.min(60000, r.data.reessayes * 2500));
      } else {
        toast[r.data.echecs ? 'warning' : 'success'](`${r.data.reussis} renvoyé(s) sur ${r.data.reessayes}${r.data.echecs ? ` — ${r.data.echecs} toujours en échec` : ''}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Relance impossible'); }
    setBusy(false);
    charger();
  };
  if (!c) return <div className="flex h-full items-center justify-center bg-[#f6f8fb]"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const [label, cls] = STATUT_CAMPAGNE[c.statut] || ['Hors campagne', 'bg-slate-100 text-slate-600'];
  return (
    <div className="h-full overflow-y-auto bg-[#f6f8fb] p-4" data-testid="klarys-campagne-detail">
      <button type="button" onClick={onRetour} className="mb-3 flex items-center gap-1 text-xs font-medium text-[#0a3d62] hover:underline" data-testid="klarys-campagne-retour">
        <ArrowLeft className="h-3.5 w-3.5" /> Toutes les campagnes
      </button>
      <div className="mb-3 flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold text-slate-900" data-testid="klarys-campagne-titre">{c.titre}</p>
          <p className="text-[11px] text-slate-500">{c.date ? `${paris(c.date)} · ` : ''}{c.critere ? `${c.critere} · ` : ''}{c.par || ''}</p>
          <p className="mt-1 text-xs text-slate-700" data-testid="klarys-campagne-stats">
            <b>{c.envoyes}</b> envoyé(s) · <b>{c.ouverts}</b> ouvert(s) ({c.taux_ouverture} %) · <b className={c.echecs ? 'text-rose-600' : ''}>{c.echecs}</b> échec(s)
          </p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{label}</span>
        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="klarys-campagne-export"
          onClick={() => telecharger(klarysUrl(`/envois/campagnes/${cid}/export.xlsx?periode=${periode}`), `envois_klarys_${cid.slice(0, 8)}.xlsx`, headers)}>
          <FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Export Excel
        </Button>
        {c.echecs > 0 && (
          <Button size="sm" className="h-8 bg-rose-600 text-xs hover:bg-rose-700" disabled={busy} onClick={toutReessayer} data-testid="klarys-campagne-reessayer-tout">
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RotateCw className="mr-1 h-3.5 w-3.5" />} Réessayer tous les échecs
          </Button>
        )}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        {!c.lignes.length ? <p className="text-sm text-slate-500" data-testid="klarys-campagne-vide">Aucun envoi enregistré pour cette campagne{c.statut === 'programmee' ? ' (programmée, pas encore partie)' : ''}.</p> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
              <th className="pb-1 font-semibold">Membre</th><th className="pb-1 font-semibold">Document</th><th className="pb-1 font-semibold">Statut</th><th className="pb-1 font-semibold">Lecture</th><th />
            </tr></thead>
            <tbody>{c.lignes.map((l, i) => <Ligne key={`${l.ged_id}-${i}`} l={l} i={i} headers={headers} onMaj={charger} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};
